import type { Lang } from './types';

// Every user-facing string, Spanish first. Where the design pairs a Spanish
// label with its English gloss ("enviadas · outgoing"), the Spanish side keeps
// the pairing and the English side drops it, exactly as the design does.
const strings = {
  navMailbox: ['Buzón', 'Mailbox'],
  navTrack: ['Rastreo', 'Tracking'],
  navWrite: ['Redactar', 'Write'],
  navStamps: ['Sellos', 'Stamps'],
  navProfile: ['Perfil', 'Profile'],

  mailboxEmpty: [
    'Todavía no ha llegado nada. Las postales tardan unos días: eso es parte del regalo.',
    'Nothing has arrived yet. Postcards take a few days: that is part of the gift.',
  ],
  newBadge: ['nueva', 'new'],
  message: ['mensaje', 'message'],
  scanFrom: ['escaneo de {who}', 'scan from {who}'],
  postmarkWord: ['correo', 'post'],
  to: ['para', 'to'],
  close: ['cerrar', 'close'],
  flip: ['girar la postal', 'turn the postcard over'],

  trackTitle: ['Rastreo', 'Tracking'],
  trackSub: ['lo que enviaste · your outgoing mail', 'your outgoing mail'],
  outgoing: ['enviadas · outgoing', 'outgoing'],
  emptyOut: ['Nada en camino ahora mismo.', 'Nothing in the mail right now.'],
  arrives: ['llega', 'arrives'],
  cancel: ['cancelar', 'cancel'],
  you: ['tú', 'you'],
  confirmTitle: ['¿Sacar la postal del correo?', 'Pull this postcard out of the mail?'],
  confirmBody: ['{who} no la recibirá. No se puede deshacer.', "{who} won't receive it. This can't be undone."],
  confirmNo: ['no, dejarla', 'no, leave it'],
  confirmYes: ['sí, cancelar', 'yes, cancel'],

  stepFront: ['El frente', 'The front'],
  stepFrontSub: ['sube tu foto', 'upload your picture'],
  stepNote: ['Tu mensaje', 'Your message'],
  stepNoteSub: ['escribe o escanea', 'write or scan your note'],
  frontIdle: ['sube tu foto', 'upload a picture'],
  frontIdleSub: ['toca para elegir del teléfono', 'tap to choose from your phone'],
  frontBusy: ['subiendo…', 'uploading…'],
  frontReady: ['foto lista', 'photo attached'],
  frontReadySub: ['lista para escribir', 'ready to write'],
  frontCaption: ['foto — {place}', 'photo — {place}'],
  notePlaceholder: ['Querida ñaña…', 'Dear ñaña…'],
  uploadNote: ['subir tu letra', 'upload your note'],
  scannedNote: ['tu letra escaneada · toca para escribir', 'your scanned note · tap to type'],
  stampBox1: ['toca', 'tap for'],
  stampBox2: ['sello', 'stamp'],
  tapToChoose: ['toca para elegir', 'tap to choose'],
  chooseWho: ['¿para quién? · choose a handle', 'who is it for? · choose a handle'],
  noFriendsToPick: ['Añade amigos en tu perfil para escribirles.', 'Add friends on your profile to write to them.'],
  stampsFrom: ['sellos de', 'stamps from'],
  tapStamp: ['toca un sello para pegarlo', 'tap a stamp to affix it'],
  send: ['depositar en el buzón', 'drop in the mailbox'],
  back: ['atrás', 'back'],
  needNote: ['Escribe o sube tu mensaje primero.', 'Write or upload your message first.'],
  needRecipient: ['Elige para quién es.', 'Choose who it is for.'],
  needStamp: ['Falta el sello.', 'The stamp is missing.'],

  sentTitle: ['Depositada', 'In the mail'],
  sentLine: ['rumbo a {country} · llega en {eta}', 'headed to {country} · arrives in {eta}'],
  oneDay: ['1 día', '1 day'],
  manyDays: ['{n} días', '{n} days'],
  sentBody: [
    'Tu postal a {who} va en camino. Nada de prisa: eso es parte del regalo.',
    'Your postcard to {who} is on its way. No rush: that is part of the gift.',
  ],
  toMailbox: ['al buzón', 'to mailbox'],

  stampsTitle: ['Sellos', 'Stamps'],
  collected: ['{n} coleccionados', '{n} collected'],
  albumEmpty: [
    'Tu álbum está vacío. Cada postal que recibes trae un sello.',
    'Your album is empty. Every postcard you receive brings a stamp.',
  ],
  seriesLine: ['serie {series} · {value}', '{series} series · {value}'],
  receivedFrom: ['Recibido en una postal de {who}.', 'Received on a postcard from {who}.'],
  closeHint: ['toca para cerrar', 'tap to close'],

  addFriend: ['añadir amigo · add a friend', 'add a friend'],
  invite: ['añadir', 'add'],
  friendsCount: ['amigos · {n} en tu libreta', 'friends · {n} in your address book'],
  statusFriends: ['amigos', 'friends'],
  friendAdded: ['{who} ya está en tu libreta.', '{who} is now in your address book.'],
  noFriends: ['Tu libreta está vacía. Añade a alguien con su usuario.', 'Your address book is empty. Add someone by their handle.'],
  yourHandle: ['tu dirección · your handle', 'your handle'],
  uploadPhoto1: ['sube tu foto', 'upload photo'],
  uploadPhoto2: ['upload photo', 'tap to choose'],
  edit: ['editar', 'edit'],
  signOut: ['cerrar sesión', 'sign out'],
  notifyTurnOn: ['avísame cuando llegue una postal', 'notify me when a postcard arrives'],
  notifyTurnOff: ['avisos activados · desactivar', 'notifications on · turn off'],
  notifyBlocked: ['avisos bloqueados en el navegador', 'notifications are blocked in your browser'],
  notifyInstall: [
    'para recibir avisos, añade la app a tu pantalla de inicio',
    'to get notifications, add the app to your home screen',
  ],
  notifyEnabled: ['Te avisaremos cuando llegue una postal.', "We'll let you know when a postcard arrives."],
  editTitle: ['Tu dirección', 'Your address'],
  handleLabel: ['usuario', 'handle'],
  countryLabel: ['país', 'country'],
  chooseCountry: ['elige tu país', 'choose your country'],
  welcomeHello: ['hola, {who}', 'hello, {who}'],
  welcomeTitle: ['¿Desde dónde escribes?', 'Where are you writing from?'],
  welcomeBody: [
    'Las postales tardan más cuanto más lejos viajan, así que necesitamos saber desde qué país escribes. Puedes cambiarlo luego en tu perfil.',
    'Postcards take longer the further they travel, so we need to know which country you write from. You can change it later on your profile.',
  ],
  welcomeGo: ['empezar', 'start'],
  save: ['guardar', 'save'],
  saved: ['Guardado.', 'Saved.'],

  loginTagline: ['postales entre amigos · ecuador', 'postcards between friends · ecuador'],
  loginBody: [
    'Escribe una postal hoy. Llega en unos días. Sin prisa, sin scroll.',
    'Write a postcard today. It arrives in a few days. No hurry, no scrolling.',
  ],
  signInGoogle: ['entrar con google', 'sign in with google'],
  signInFailed: ['No pudimos iniciar sesión. Inténtalo otra vez.', 'We could not sign you in. Please try again.'],
  devOnly: ['solo desarrollo · entrar como', 'development only · sign in as'],
  loading: ['cargando…', 'loading…'],
  genericError: ['Algo salió mal.', 'Something went wrong.'],
} as const;

export type StringKey = keyof typeof strings;

export function makeT(lang: Lang) {
  const i = lang === 'en' ? 1 : 0;
  return (key: StringKey, vars?: Record<string, string | number>): string => {
    let s: string = strings[key][i];
    if (vars) for (const [k, v] of Object.entries(vars)) s = s.split(`{${k}}`).join(String(v));
    return s;
  };
}

export type T = ReturnType<typeof makeT>;
